/**
 * Native DOM selection hygiene for the canvas.
 *
 * Safari paints selection "gaps" — whole note bodies and the empty canvas
 * between cards — for any document range that spans nodes, and such a range
 * survives node clicks and drags (React Flow prevents the mousedown default
 * that would replace it). These helpers drop a range that no live editor
 * owns so nothing stray is ever painted.
 */

function isEditableElement(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return (
    el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA"
  );
}

/**
 * Drop a ranged selection when no editable element has focus — the range
 * cannot belong to an active edit session, so it is only ever paint.
 */
export function clearStrayDomSelection(): void {
  const sel = window.getSelection();
  if (!sel || sel.type !== "Range") return;
  if (isEditableElement(document.activeElement)) return;
  sel.removeAllRanges();
}

/**
 * Drop the selection when its anchor sits inside `root` — used when an
 * editor leaves edit mode so its last caret/range is not left behind.
 */
export function clearDomSelectionInside(
  root: Element | null | undefined,
): void {
  if (!root) return;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  if (sel.anchorNode && root.contains(sel.anchorNode)) sel.removeAllRanges();
}
